import React from 'react';
import PropTypes from 'prop-types';
import { useBrand } from '../../../BrandContext';
import Spinner from '../../../pages/Spinner';
import { useAggregatorConsumerName } from '../../../utilities/aggregatorConsumerNameProvider';
import { decisions, routes } from '../../../utilities/constants';
import pushToDataLayer, { EVENTS } from '../../../utilities/dataLayer';
import { addNewRelicPageAction } from '../../../utilities/newRelic';
import getResultPage from './getResult';
import { agreementUrlBuilder } from './agreementUrlBuilder';
import ObStartRedirect from './ObStartRedirect';
import { useNavigate } from 'react-router';
import {
    OB_ACCEPT_CONTROL_VARIANT,
    OB_ACCEPT_OPTED_IN_VARIANT
} from '../../experiments/variants/OpenBankingAcceptRegionTest/variants';

/* eslint-disable-next-line complexity */ // This is because there are 6 decline scenarios so refactoring to meet complexity makes function harder to read
const isDecline = (reason: any) =>
    reason === decisions.INFORMATION_MISMATCH ||
    reason === decisions.SCORE_DECLINE ||
    reason === decisions.ALREADY_APPLIED_DECLINE;

const sendNewRelicActions = (firstName: any, requoteOptIn: any) => {
    // @ts-expect-error TS2554: Expected 2 arguments, but got 1.
    if (firstName) addNewRelicPageAction('First name not recieved');
    // @ts-expect-error TS2554: Expected 2 arguments, but got 1.
    if (!requoteOptIn) addNewRelicPageAction('Requote opt in preferences not recieved');
};

const getLoadingPage = (acceptRegionVariant: any, reason: any, responseData: any, pcr: any, agreementUrl: any) => {
    if (acceptRegionVariant === OB_ACCEPT_OPTED_IN_VARIANT && reason === decisions.MATCH_AND_OFFER) {
        return (
            <ObStartRedirect
                agreementUrl={agreementUrl}
                ccsCode={responseData.ccsCode}
                sParameter={responseData.sParameter}
                pcr={pcr}
            />
        );
    }
    return <Spinner agreementUrl={agreementUrl} />;
};

const redirectToAgreementsCheck = (agreementUrl: any, acceptRegionVariant: any) => {
    if (!acceptRegionVariant || acceptRegionVariant === OB_ACCEPT_CONTROL_VARIANT) {
        window.location.assign(agreementUrl);
    }
};

const isAllowedObPathname = (openBankingUrl: any) => {
    if (!openBankingUrl) return false;
    try {
        const { pathname } = new URL(openBankingUrl);
        return pathname === routes.obLanding || pathname === routes.obPending;
    } catch {
        return false;
    }
};

const Result = ({
    responseData = {},
    submitting = false,
    pcr = '',
    campaign = '',
    channel = '',
    vendorCode = '',
    acceptRegionVariant = ''
}) => {
    // @ts-expect-error TS2339: Property 'reason' does not exist on type ''.
    const { reason, userData, requoteOptIn, openBankingDecisionIndicator, openBankingUrl } = responseData;
    const brand = useBrand();
    const navigate = useNavigate();
    // @ts-expect-error TS2488: Type '' must have a '[Symbol.iterator]()' method that returns an iterator.
    const [aggregatorConsumerName] = useAggregatorConsumerName();
    sendNewRelicActions(userData?.firstName, requoteOptIn);

    const agreementUrl = React.useMemo(() => {
        const {
            // @ts-expect-error TS2339: Property 'ccsCode' does not exist on type ''.
            ccsCode: ccsCodeQuotation,
            // @ts-expect-error TS2339: Property 'sParameter' does not exist on type ''.
            sParameter: sParameterQuotation,
            // @ts-expect-error TS2339: Property 'agreementUrl' does not exist on type ''.
            agreementUrl: baseAgreementUrl
        } = responseData;
        if (!baseAgreementUrl) return undefined;
        return agreementUrlBuilder({
            agreementUrl: baseAgreementUrl,
            sParameterQuotation,
            ccsCodeQuotation,
            brand,
            aggregatorConsumerName,
            pcr,
            channel,
            campaign,
            vendorCode
        }).href;
    }, [responseData, brand, aggregatorConsumerName, pcr, channel, campaign, vendorCode]);

    React.useEffect(() => {
        if (reason) addNewRelicPageAction('Result', { decision: reason });
        if (openBankingDecisionIndicator) {
            addNewRelicPageAction('openBankingDecisionIndicator', { indicator: openBankingDecisionIndicator });
        }
    }, [reason, openBankingDecisionIndicator]);

    React.useEffect(() => {
        if (reason === decisions.MATCH_AND_OFFER) {
            pushToDataLayer({
                event: EVENTS.SUBMISSION_RESULT,
                result: 'approved'
            });
            redirectToAgreementsCheck(agreementUrl, acceptRegionVariant);
        } else if (isDecline(reason)) {
            pushToDataLayer({
                event: EVENTS.SUBMISSION_RESULT,
                result: 'declined'
            });
            if (isAllowedObPathname(openBankingUrl)) {
                const obUrl = new URL(openBankingUrl);
                navigate(obUrl.pathname + obUrl.search);
            }
        } else if (reason === decisions.ERROR) {
            pushToDataLayer({
                event: EVENTS.SUBMISSION_RESULT,
                result: 'error'
            });
        }
    }, [submitting, reason, openBankingUrl, acceptRegionVariant, agreementUrl]);

    // The request is still in flight; This is the very first thing rendered on every submission.
    if (submitting) {
        return <Spinner />;
    }
    // The response has landed, but is still in a transitional state; not a terminal page.
    if (reason === decisions.MATCH_AND_OFFER || isAllowedObPathname(openBankingUrl)) {
        return getLoadingPage(acceptRegionVariant, reason, responseData, pcr, agreementUrl);
    }
    // openBankingUrl was expected to continue the OB journey but doesn't point at an allowed
    // internal route
    if (openBankingUrl && !isAllowedObPathname(openBankingUrl)) {
        throw new Error('openBankingUrl did not match an allowed internal route');
    }
    // No response AND not ;ubmitting. Sends the user back to the QuickCheck form via window.location.assign.
    if (!reason) {
        const url = routes.baseWithQuery(window.location.search);
        return window.location.assign(url);
    }
    // Everything else; eg. decline and error.
    // @ts-expect-error TS2554: Expected 4 arguments, but got 3.
    return getResultPage(reason, userData, requoteOptIn);
};

Result.propTypes = {
    responseData: PropTypes.shape({
        reason: PropTypes.string,
        userData: PropTypes.object,
        token: PropTypes.string,
        ccsCode: PropTypes.string,
        sParameter: PropTypes.string,
        agreementUrl: PropTypes.string,
        requoteOptIn: PropTypes.string
    }),
    submitting: PropTypes.bool,
    pcr: PropTypes.string,
    campaign: PropTypes.string,
    vendorCode: PropTypes.string,
    channel: PropTypes.string,
    acceptRegionVariant: PropTypes.string
};

export default Result;
